import { getAuthHeaders } from '@/identity.js';

const GENERAL_FEEDBACK_ENDPOINT = '/v1/conversations/feedback';

/** Mirrors support.ts's own TICKET_TIMEOUT_MS — same gateway, same slow part. */
const GENERAL_FEEDBACK_TIMEOUT_MS = 20000;

/**
 * A deadline for one fetch, as an `AbortController` rather than `AbortSignal.timeout` (missing in
 * some of the runtimes this app is served in). Returns the signal and the timer to clear.
 */
function deadline(ms: number): { signal: AbortSignal; done: () => void } {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, done: () => clearTimeout(timer) };
}

async function errorMessage(response: Response): Promise<string> {
  const text = await response.text().catch(() => '');
  try {
    const body = JSON.parse(text) as { message?: string | string[] };
    if (Array.isArray(body.message)) return body.message.join(', ');
    if (typeof body.message === 'string' && body.message.trim()) {
      return body.message;
    }
  } catch {}
  return text.trim() || `Could not send feedback (${response.status}).`;
}

export async function submitGeneralFeedback(
  comment: string,
): Promise<{ id: string }> {
  const headers = await getAuthHeaders();
  const { signal, done } = deadline(GENERAL_FEEDBACK_TIMEOUT_MS);
  try {
    const response = await fetch(GENERAL_FEEDBACK_ENDPOINT, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ comment }),
      signal,
    });
    if (!response.ok) throw new Error(await errorMessage(response));
    return (await response.json()) as { id: string };
  } catch (error) {
    if (signal.aborted) {
      throw new Error(
        'The server did not answer in time. Your feedback may not have been sent.',
      );
    }
    throw error;
  } finally {
    done();
  }
}
